/**
 * Admin Users Hooks
 *
 * Re-exports all admin user hooks and provides the UniversalEntityPage adapter.
 *
 * Split into focused modules for maintainability:
 *   - admin-users-queries.ts   — useAdminUsers, useAdminUser
 *   - admin-users-mutations.ts — useCreateAdminUser, useUpdateAdminUser, useDeleteAdminUser
 *   - admin-users-access.ts    — grant/revoke employee & client access
 */
export * from "./admin-users-queries";
export * from "./admin-users-mutations";
export * from "./admin-users-access";

import { useAdminUsers } from "./admin-users-queries";
import { useAdminUser } from "./admin-users-queries";
import {
  useCreateAdminUser,
  useUpdateAdminUser,
  useDeleteAdminUser,
} from "./admin-users-mutations";
import type { User } from "@/lib/types/user";
import type { App_Users } from "@/src/gql/graphql";
import type { EntityHooks } from "@/components/shared/UniversalCRMTable/types/shared";

/** Params accepted by the admin-user list/pagination hooks. */
interface AdminUsersParams extends Record<string, unknown> {
  search?: string;
  role?: string;
  is_active?: boolean;
  page?: number;
  pageSize?: number;
}

/**
 * The query layer returns the hand-rolled `User` row shape; the
 * UniversalEntityPage contract is the codegen `App_Users` entity
 * (per ENTITY_HOOKS_USE_CODEGEN_TYPES). Bridge the two at the data boundary.
 */
const asEmployeeEntities = (users: User[] | undefined): App_Users[] =>
  (users ?? []) as unknown as App_Users[];

const asEmployeeEntity = (user: User | undefined): App_Users | undefined =>
  user ? (user as unknown as App_Users) : undefined;

/**
 * Entity hooks adapter for UniversalEntityPage integration.
 */
export const useAdminUserHooks = (): EntityHooks<App_Users, AdminUsersParams> => ({
  usePaginated: (params?: Partial<AdminUsersParams>) => {
    const result = useAdminUsers(params);
    const data = asEmployeeEntities(result.data);
    return {
      data: result.data ? { data, totalCount: data.length } : undefined,
      isLoading: result.isLoading,
      error: result.error,
      refetch: result.refetch,
    };
  },
  useInfinite: (_params?: Partial<AdminUsersParams>) => ({
    data: undefined,
    isLoading: false,
    error: null,
    refetch: () => {},
    hasNextPage: false,
    isFetchingNextPage: false,
    fetchNextPage: () => {},
  }),
  useSingle: (id: string, _options?: { enabled?: boolean }) => {
    const result = useAdminUser(id);
    return {
      data: asEmployeeEntity(result.data),
      isLoading: result.isLoading,
      error: result.error,
      refetch: result.refetch,
    };
  },
  useCreate: () => {
    const mutation = useCreateAdminUser();
    return {
      mutateAsync: async (data: Partial<App_Users> & { id?: string }) =>
        asEmployeeEntity(await mutation.mutateAsync(data as unknown as Partial<User>)) as App_Users,
      isPending: mutation.isPending,
    };
  },
  useUpdate: () => {
    const mutation = useUpdateAdminUser();
    return {
      mutateAsync: async (data: Partial<App_Users> & { id: string }) =>
        asEmployeeEntity(
          await mutation.mutateAsync({
            id: data.id,
            updates: data as unknown as Partial<User>,
          })
        ) as App_Users,
      isPending: mutation.isPending,
    };
  },
  useDelete: () => {
    const mutation = useDeleteAdminUser();
    return {
      mutateAsync: (id: string) => mutation.mutateAsync(id).then(() => id),
      isPending: mutation.isPending,
    };
  },
});
